/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentProps, ReactElement } from 'react';

import { css, jsx } from '@atlaskit/css';
import type { Heading as OriginalHeading } from '@atlaskit/renderer/nodes';

import type { LightNodeProps } from '../LightNodeObserver.ts';

const headingAnchorSlotStyles = css({
	display: 'inline-block',
	height: '1rem',
	verticalAlign: 'bottom',
	width: '1rem',
});

const headingTags = {
	1: 'h1',
	2: 'h2',
	3: 'h3',
	4: 'h4',
	5: 'h5',
	6: 'h6',
} as const;

export const Heading = (
	props: ComponentProps<typeof OriginalHeading> & LightNodeProps,
): ReactElement => {
	const HeadingTag = headingTags[props.level];
	const anchorSlot = props.showAnchorLink && props.headingId && (
		<span
			aria-hidden
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Reuse Platform's canonical heading-anchor spacing hook.
			className="heading-anchor-wrapper"
		>
			<span css={headingAnchorSlotStyles} />
		</span>
	);
	const hasRightAlignment = props.marks?.some(
		(mark) => mark.type.name === 'alignment' && mark.attrs.align === 'end',
	);

	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Reuse Platform's canonical heading layout and level-specific spacing.
			className="renderer-heading-wrapper"
			data-level={props.level}
		>
			{hasRightAlignment && anchorSlot}
			<HeadingTag
				ref={props.lightNodeRef}
				data-as-inline={props.asInline}
				data-local-id={props.localId}
				data-renderer-start-pos={props.dataAttributes?.['data-renderer-start-pos']}
				id={props.invisible ? undefined : props.headingId}
				tabIndex={-1}
			>
				{props.children}
			</HeadingTag>
			{!hasRightAlignment && anchorSlot}
		</div>
	);
};
